import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Award, Sparkles, Printer } from "lucide-react";
import { JR_BIRDS } from "@/utils/jrDemoState";

/**
 * Junior CEO Certificate Preview — visual-only.
 *
 * Route: /elementary/demo/certificate-preview
 *
 * Supports optional querystring personalization (display-only, nothing
 * saved): ?name=...&business=...&bird=eagle|parrot|dove|owl
 *
 * Pilot-grade preview so schools, funders, and students can see the
 * reward before we wire real generation. No backend, no PDF export yet.
 * "Preview only" watermark is intentional and stays regardless of params.
 */
export default function ElementaryCertificatePreview() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const MAX = 60; // defensive cap so URL abuse can't blow out the layout
  const clip = (s) => (s || "").toString().slice(0, MAX).trim();

  const displayName = clip(params.get("name")) || "Junior CEO";
  const displayBusiness = clip(params.get("business")) || "Pencil Power Station";
  const birdKey = (params.get("bird") || "").toLowerCase();
  const bird = JR_BIRDS[birdKey] || JR_BIRDS.eagle;

  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-amber-50 to-white print:bg-white">
      {/* Top bar (hidden in print) */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/elementary/demo")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            data-testid="cert-back-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to CEO City
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            data-testid="cert-print"
          >
            <Printer className="w-4 h-4" />
            Print preview
          </button>
        </div>
      </nav>

      <main className="pt-28 pb-16 px-4 print:pt-0">
        <div className="max-w-4xl mx-auto">
          {/* Eyebrow */}
          <div className="text-center mb-6 print:hidden">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Preview only · Visual concept
            </div>
          </div>

          {/* Certificate */}
          <article
            className="relative bg-white rounded-[28px] ring-4 ring-yellow-300 shadow-2xl p-8 sm:p-14 text-center overflow-hidden print:shadow-none print:ring-2"
            data-testid="junior-ceo-certificate"
          >
            {/* Decorative corner accents */}
            <span
              className="absolute -top-10 -left-10 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl"
              aria-hidden="true"
            />
            <span
              className="absolute -bottom-12 -right-10 w-48 h-48 bg-orange-400/20 rounded-full blur-3xl"
              aria-hidden="true"
            />

            {/* Preview watermark */}
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
              aria-hidden="true"
            >
              <span className="text-[7rem] sm:text-[10rem] font-black uppercase tracking-widest text-yellow-100/70 rotate-[-18deg]">
                Preview
              </span>
            </div>

            {/* Content */}
            <div className="relative">
              <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-orange-600 mb-3">
                The Lemonade Stand · Lemonade Leaders Jr.
              </p>

              <div className="flex justify-center mb-4">
                <div className="bg-yellow-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                  <Award className="w-9 h-9" />
                </div>
              </div>

              <h1 className="text-3xl sm:text-5xl font-black text-gray-900 mb-3 leading-tight">
                Junior CEO Certificate
              </h1>

              <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">
                Proudly awarded to
              </p>
              <p className="text-3xl sm:text-4xl font-bold text-yellow-600 mb-6 font-serif italic" data-testid="cert-name">
                {displayName}
              </p>

              <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto mb-8 leading-relaxed">
                For completing the <strong>Mini CEO Adventure</strong> — nine
                missions of problem solving, teamwork, creativity, money
                smarts, communication, and courage.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
                <div className="bg-yellow-50 rounded-2xl p-4">
                  <p className="text-[10px] font-bold text-yellow-700 uppercase tracking-wide mb-1">
                    Biz Bird
                  </p>
                  <p className="text-gray-900 font-bold flex items-center gap-2" data-testid="cert-bird">
                    {bird.image ? (
                      <img
                        src={bird.image}
                        alt=""
                        aria-hidden="true"
                        className="w-7 h-7 rounded-full object-contain bg-white ring-1 ring-yellow-200"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="text-xl" aria-hidden="true">{bird.emoji}</span>
                    )}
                    <span>{bird.name}</span>
                  </p>
                </div>
                <div className="bg-orange-50 rounded-2xl p-4">
                  <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wide mb-1">
                    Business Idea
                  </p>
                  <p className="text-gray-900 font-bold" data-testid="cert-business">{displayBusiness}</p>
                </div>
                <div className="bg-green-50 rounded-2xl p-4">
                  <p className="text-[10px] font-bold text-green-700 uppercase tracking-wide mb-1">
                    Date
                  </p>
                  <p className="text-gray-900 font-bold">{today}</p>
                </div>
              </div>

              {/* Signatures */}
              <div className="grid sm:grid-cols-2 gap-8 pt-6 max-w-2xl mx-auto">
                <div>
                  <div className="border-b-2 border-gray-300 mb-2 h-8 flex items-end justify-center">
                    <span className="font-serif italic text-gray-500 text-lg">LemonBot Jr.</span>
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Your AI Coach
                  </p>
                </div>
                <div>
                  <div className="border-b-2 border-gray-300 mb-2 h-8 flex items-end justify-center">
                    <span className="font-serif italic text-gray-500 text-lg">The WE Mentality</span>
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Lemonade Stand Business Plan
                  </p>
                </div>
              </div>

              <p className="mt-8 text-[11px] text-gray-400 uppercase tracking-wider">
                Preview only · Real certificate unlocks after all 9 missions
              </p>
            </div>
          </article>

          {/* CTA strip */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center print:hidden">
            <button
              onClick={() => navigate("/elementary/demo")}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-full font-bold transition-colors"
              data-testid="cert-continue-missions"
            >
              Back to CEO City
            </button>
            <button
              onClick={() => navigate("/lemonade-leaders-jr")}
              className="bg-white ring-2 ring-yellow-300 hover:ring-yellow-500 text-gray-800 px-6 py-3 rounded-full font-bold transition-colors"
              data-testid="cert-learn-more"
            >
              About Lemonade Leaders Jr.
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
